import { Award, Briefcase, Lock, ShieldCheck, Star, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	getPortfolioTemplate,
	normalizeContractorTemplate,
	renderPublicTemplateHeader,
} from "@/features/portfolio/template-definitions";
import { findTrade } from "@/lib/constants/markets";
import { resolveMediaUrl } from "@/lib/utils";
import {
	getPublicContractorProfile,
	getPublicContractorProfileProject,
	type PublicContractorProfileResponse,
	type PublicContractorProjectResponse,
} from "../services/contractor-profile-api.service";

function ProjectDetailsModal({
	data,
	onClose,
	onRequireAuth,
}: {
	data: PublicContractorProjectResponse;
	onClose: () => void;
	onRequireAuth: () => void;
}): React.ReactElement {
	const { t } = useTranslation();
	const project = data.project;
	const imageUrls = project.imageUrls || [];
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-md">
			<button
				type="button"
				aria-label="Close project details"
				className="absolute inset-0 cursor-default"
				onClick={onClose}
			/>
			<Card
				className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-3xl border-0 bg-dashboard-paper shadow-2xl"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header Image */}
				<div className="relative h-64 overflow-hidden sm:h-80">
					<img
						src={resolveMediaUrl(project.thumbnailUrl)}
						alt={project.title}
						className="h-full w-full object-cover"
					/>
					<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
					<div className="absolute bottom-0 left-0 right-0 p-6">
						<CardTitle className="text-2xl font-bold text-dashboard-on-fill sm:text-3xl">
							{project.title}
						</CardTitle>
						<CardDescription className="mt-2 text-base text-dashboard-on-fill/80">
							{project.shortDescription}
						</CardDescription>
					</div>
					<button
						type="button"
						aria-label="Close"
						onClick={onClose}
						className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/30 text-dashboard-on-fill backdrop-blur-sm transition-colors hover:bg-black/50"
					>
						<svg
							className="h-5 w-5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<title>Close</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				<CardContent className="space-y-6 p-6">
					{data.previewOnly ? (
						<div className="space-y-5 rounded-2xl bg-gradient-to-br from-dashboard-surface to-dashboard-divider p-5 sm:p-8 text-center">
							<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-dashboard-accent to-dashboard-accent-hover text-dashboard-on-fill shadow-lg shadow-dashboard-warm-200 dark:shadow-dashboard-warm-200/40">
								<Lock className="h-7 w-7" />
							</div>
							<div>
								<p className="text-lg font-semibold text-dashboard-text">
									Sign up to view full project details
								</p>
								<p className="mt-2 text-sm text-dashboard-muted">
									All project images and detailed information are available
									after creating an account.
								</p>
							</div>
							<div className="flex flex-wrap justify-center gap-3 pt-2">
								<Button
									className="bg-dashboard-accent hover:bg-dashboard-accent-hover text-dashboard-on-fill"
									onClick={onRequireAuth}
								>
									Create Account
								</Button>
								<Button
									variant="outline"
									className="border-dashboard-border"
									onClick={onRequireAuth}
								>
									Log in
								</Button>
							</div>
						</div>
					) : (
						<div>
							<h4 className="mb-4 text-sm font-semibold uppercase tracking-widest text-dashboard-placeholder">
								Project Gallery
							</h4>
							<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
								{imageUrls.map((url, index) => (
									<button
										type="button"
										key={url}
										aria-label={t("workPhotos.open", {
											index: index + 1,
											total: imageUrls.length,
										})}
										onClick={() => setLightboxIndex(index)}
										className="group cursor-zoom-in overflow-hidden rounded-xl border-0 bg-transparent p-0"
									>
										<img
											src={resolveMediaUrl(url)}
											alt={project.title}
											className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-110"
										/>
									</button>
								))}
							</div>
						</div>
					)}

					<div className="flex justify-end border-t border-dashboard-border-soft pt-4">
						<Button
							variant="ghost"
							className="text-dashboard-muted hover:text-dashboard-text"
							onClick={onClose}
						>
							Close
						</Button>
					</div>
				</CardContent>
			</Card>
			<ImageLightbox
				images={imageUrls.map((src, index) => ({
					src,
					alt: t("workPhotos.alt", { name: project.title, index: index + 1 }),
				}))}
				openIndex={lightboxIndex}
				onOpenIndexChange={setLightboxIndex}
			/>
		</div>
	);
}

export function PublicContractorProfilePage(): React.ReactElement {
	const { slug = "" } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { isAuthenticated } = useAuthStore();

	const [loading, setLoading] = useState(true);
	const [profile, setProfile] =
		useState<PublicContractorProfileResponse | null>(null);
	const [selected, setSelected] =
		useState<PublicContractorProjectResponse | null>(null);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
		null,
	);

	const requestedProjectId = useMemo(
		() => searchParams.get("project"),
		[searchParams],
	);

	const [loadFailed, setLoadFailed] = useState(false);

	const templateDef = useMemo(() => {
		const normalized = normalizeContractorTemplate(profile?.profile.template);
		return getPortfolioTemplate(normalized);
	}, [profile?.profile.template]);

	const load = useCallback(async () => {
		setLoading(true);
		setLoadFailed(false);
		try {
			setProfile(await getPublicContractorProfile(slug));
		} catch {
			setProfile(null);
			setLoadFailed(true);
		} finally {
			setLoading(false);
		}
	}, [slug]);

	useEffect(() => {
		if (slug) {
			void load();
		}
	}, [slug, load]);

	useEffect(() => {
		async function loadProject(projectId: string) {
			const response = await getPublicContractorProfileProject(slug, projectId);
			if (response) {
				setSelected(response);
				setSelectedProjectId(projectId);
			}
		}

		if (requestedProjectId && profile) {
			void loadProject(requestedProjectId);
		}
	}, [requestedProjectId, profile, slug]);

	async function openProject(projectId: string) {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("project", projectId);
			return next;
		});

		const response = await getPublicContractorProfileProject(slug, projectId);

		if (response) {
			setSelected(response);
			setSelectedProjectId(projectId);
		}
	}

	function closeModal() {
		setSelected(null);
		setSelectedProjectId(null);
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.delete("project");
			return next;
		});
	}

	function requireAuth() {
		const projectId = selectedProjectId || requestedProjectId;
		const redirectTo = `/contractor-profile/${slug}${projectId ? `?project=${projectId}` : ""}`;
		navigate("/login", { state: { redirectTo } });
	}

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-b from-dashboard-surface to-dashboard-paper p-4 sm:p-6">
				<div className="mx-auto max-w-7xl space-y-6">
					{/* Header Skeleton */}
					<div className="overflow-hidden rounded-2xl bg-gradient-to-br from-dashboard-dark to-dashboard-dark-elevated p-5 sm:p-8">
						<div className="flex items-start gap-6">
							<Skeleton className="h-24 w-24 rounded-2xl bg-dashboard-on-fill/20" />
							<div className="flex-1 space-y-4">
								<Skeleton className="h-6 w-32 rounded-full bg-dashboard-on-fill/20" />
								<Skeleton className="h-10 w-2/3 rounded-lg bg-dashboard-on-fill/20" />
								<Skeleton className="h-5 w-full rounded-lg bg-dashboard-on-fill/20" />
								<Skeleton className="h-5 w-3/4 rounded-lg bg-dashboard-on-fill/20" />
							</div>
						</div>
					</div>
					{/* Content Skeleton */}
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						<Skeleton className="h-80 rounded-2xl" />
						<Skeleton className="h-80 rounded-2xl" />
						<Skeleton className="h-80 rounded-2xl" />
					</div>
				</div>
			</div>
		);
	}

	if (!profile) {
		return (
			<div className="flex min-h-[70vh] items-center justify-center p-6 bg-gradient-to-b from-dashboard-surface to-dashboard-paper">
				<Card className="w-full max-w-md rounded-2xl border-0 bg-dashboard-paper shadow-xl shadow-dashboard-border/50 dark:shadow-dashboard-shadow-ink/40">
					<CardHeader className="items-center text-center">
						<div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-dashboard-pill-risk-bg">
							<ShieldCheck className="h-8 w-8 text-dashboard-pill-risk" />
						</div>
						<CardTitle className="text-xl font-bold text-dashboard-text">
							{loadFailed ? "Couldn't load this profile" : "Profile not found"}
						</CardTitle>
						<CardDescription className="mt-2 text-sm text-dashboard-muted">
							{loadFailed
								? "There was a connection problem. Check your connection and try again."
								: "The link may be invalid, or the profile may not be published."}
						</CardDescription>
					</CardHeader>
					<CardContent className="flex flex-col gap-2 p-6 pt-0">
						{loadFailed && (
							<Button
								onClick={() => void load()}
								className="w-full bg-dashboard-accent hover:bg-dashboard-accent-hover text-dashboard-on-fill font-medium py-2 rounded-xl"
							>
								Retry
							</Button>
						)}
						<Button
							variant="outline"
							onClick={() => navigate(-1)}
							className="w-full"
						>
							Go Back
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className={templateDef.pageWrapperClass}>
			{renderPublicTemplateHeader(templateDef.id, {
				designer: {
					id: profile.contractor.id,
					name: profile.company.name || profile.contractor.name,
					firmName: profile.company.name || undefined,
					profilePictureUrl:
						profile.company.logoUrl ||
						profile.contractor.profilePictureUrl ||
						undefined,
					bio: profile.company.description || undefined,
					isVerified: profile.contractor.isVerified,
				},
				projectCount: profile.projects.length,
				signedIn: isAuthenticated(),
				templateName: templateDef.name,
				templateSummary: templateDef.changesSummary,
			})}

			<div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
				{/* Contractor Credentials & Overview Bar */}
				<div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-dashboard-border bg-dashboard-surface/80 p-4 sm:p-5 shadow-xs backdrop-blur-sm">
					<div className="flex flex-wrap items-center gap-3">
						<span
							className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold border ${
								profile.contractor.isVerified
									? "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300"
									: "bg-dashboard-warm-200 text-dashboard-text border-dashboard-border"
							}`}
						>
							<ShieldCheck className="h-4 w-4" />
							{profile.contractor.isVerified
								? "Verified Contractor"
								: "Published Profile"}
						</span>
						{profile.company.experienceYears ? (
							<span className="inline-flex items-center gap-1.5 rounded-full border border-dashboard-border bg-dashboard-warm-100 px-3.5 py-1.5 text-xs font-medium text-dashboard-text">
								<Award className="h-4 w-4 text-dashboard-accent" />
								{profile.company.experienceYears}+ years experience
							</span>
						) : null}
						{profile.teamMembers.length > 0 && (
							<span className="inline-flex items-center gap-1.5 rounded-full border border-dashboard-border bg-dashboard-warm-100 px-3.5 py-1.5 text-xs font-medium text-dashboard-text">
								<Users className="h-4 w-4 text-dashboard-accent" />
								{profile.teamMembers.length}{" "}
								{profile.teamMembers.length === 1
									? "Team Member"
									: "Team Members"}
							</span>
						)}
					</div>
					{profile.contractor.name ? (
						<div className="flex items-center gap-2 text-xs text-dashboard-muted">
							<span>Contractor Lead:</span>
							<span className="font-semibold text-dashboard-text">
								{profile.contractor.name}
							</span>
						</div>
					) : null}
				</div>

				{/* Specialisations & Service Areas Bar */}
				{(profile.company.specialisations.length > 0 ||
					profile.company.serviceAreas.length > 0) && (
					<div className="mb-12 flex flex-wrap items-center gap-3 rounded-2xl bg-dashboard-surface/60 p-4 border border-dashboard-border backdrop-blur-sm">
						{profile.company.serviceAreas.length > 0 && (
							<div className="flex items-center gap-2">
								<span className="text-xs font-semibold uppercase tracking-wider text-dashboard-muted">
									Areas:
								</span>
								<div className="flex flex-wrap gap-1.5">
									{profile.company.serviceAreas.map((area) => (
										<span
											key={area}
											className="rounded-md bg-dashboard-warm-100 px-2.5 py-1 text-xs font-medium text-dashboard-text"
										>
											{area}
										</span>
									))}
								</div>
							</div>
						)}
						{profile.company.specialisations.length > 0 && (
							<div className="flex items-center gap-2 sm:ml-auto">
								<span className="text-xs font-semibold uppercase tracking-wider text-dashboard-muted">
									Specialisations:
								</span>
								<div className="flex flex-wrap gap-1.5">
									{profile.company.specialisations.map((spec) => (
										<span
											key={spec}
											className="rounded-md bg-dashboard-accent/15 px-2.5 py-1 text-xs font-semibold text-dashboard-accent-dark"
										>
											{findTrade(spec)?.displayLabel ?? spec}
										</span>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Team Section */}
				{profile.teamMembers.length > 0 ? (
					<section className="mb-16">
						<div className="mb-8 flex items-center justify-between">
							<div>
								<h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-dashboard-text">
									Meet Our Team
								</h2>
								<p className="mt-1 text-sm text-dashboard-muted">
									The talented professionals behind our success
								</p>
							</div>
							<div className="hidden sm:flex items-center gap-2 rounded-full bg-dashboard-warm-100 px-4 py-2 text-sm font-medium text-dashboard-accent-dark">
								<Users className="h-4 w-4" />
								{profile.teamMembers.length} Members
							</div>
						</div>
						<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							{profile.teamMembers.map((member) => (
								<div
									key={
										member.profilePhotoUrl ||
										`${member.name}-${member.role}-${member.experienceYears ?? "unknown"}`
									}
									className="group relative overflow-hidden rounded-2xl bg-dashboard-paper shadow-xs ring-1 ring-dashboard-border-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-dashboard-border/50 hover:ring-dashboard-warm-200 dark:hover:shadow-dashboard-shadow-ink/40"
								>
									<div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-dashboard-divider to-dashboard-surface">
										{member.profilePhotoUrl ? (
											<img
												src={resolveMediaUrl(member.profilePhotoUrl)}
												alt={member.name}
												className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
											/>
										) : (
											<div className="flex h-full items-center justify-center bg-gradient-to-br from-dashboard-warm-100 to-dashboard-warm-400">
												<div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-dashboard-accent to-dashboard-accent-hover text-3xl font-bold text-dashboard-on-fill shadow-lg shadow-dashboard-warm-200 dark:shadow-dashboard-warm-200/40">
													{member.name.charAt(0).toUpperCase()}
												</div>
											</div>
										)}
										<div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
									</div>
									<div className="p-5">
										<div className="flex items-start justify-between gap-2">
											<div>
												<p className="text-lg font-semibold text-dashboard-text">
													{member.name}
												</p>
												<p className="mt-0.5 text-sm font-medium text-dashboard-accent-dark">
													{member.role}
												</p>
											</div>
											{member.experienceYears ? (
												<span className="flex items-center gap-1 rounded-full bg-dashboard-warning-bg-soft px-2.5 py-1 text-xs font-semibold text-dashboard-pill-awaiting ring-1 ring-dashboard-warning-border">
													<Star className="h-3 w-3" />
													{member.experienceYears}y
												</span>
											) : null}
										</div>
										{member.shortBio ? (
											<p className="mt-3 line-clamp-2 text-sm leading-relaxed text-dashboard-muted">
												{member.shortBio}
											</p>
										) : null}
									</div>
								</div>
							))}
						</div>
					</section>
				) : null}

				{/* Projects Section */}
				<section>
					<div className="mb-8 flex items-center justify-between">
						<div>
							<h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-dashboard-text">
								Our Projects
							</h2>
							<p className="mt-1 text-sm text-dashboard-muted">
								Explore our portfolio of completed work
							</p>
						</div>
						<div className="hidden sm:flex items-center gap-2 rounded-full bg-dashboard-warm-100 px-4 py-2 text-sm font-medium text-dashboard-accent-dark">
							<Briefcase className="h-4 w-4" />
							{profile.projects.length} Projects
						</div>
					</div>
					{profile.projects.length === 0 ? (
						<div className="rounded-2xl border border-dashed border-dashboard-border p-8 text-center bg-dashboard-surface/50">
							<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-dashboard-warm-100 text-dashboard-accent-dark">
								<Briefcase className="h-6 w-6" />
							</div>
							<p className="mt-3 text-sm font-body text-dashboard-muted">
								No projects showcased yet.
							</p>
						</div>
					) : (
						templateDef.Section({
							projects: profile.projects.map((p) => ({
								id: p.id,
								title: p.title,
								shortDescription: p.shortDescription,
								thumbnailUrl: p.thumbnailUrl,
							})),
							onOpenProject: (id) => void openProject(id),
							loadingProjectId: selectedProjectId,
							mode: "public",
						})
					)}
				</section>
			</div>

			{/* Project Details Modal */}
			{selected ? (
				<ProjectDetailsModal
					data={selected}
					onClose={closeModal}
					onRequireAuth={requireAuth}
				/>
			) : null}
		</div>
	);
}
